// src/pinball/tables/SpaceAdventureTable.ts - Space Adventure theme configuration

import { TableThemeConfig } from '../types';

export const SpaceAdventureTable: TableThemeConfig = {
  id: 'space-adventure',
  name: 'Space Adventure',
  description: 'Navigate through asteroid fields and wormholes in deep space!',
  previewImage: '',
  width: 400,
  height: 800,

  elements: [
    // Asteroid bumpers (scattered)
    {
      id: 'asteroid_1',
      type: 'pop_bumper',
      position: { x: 120, y: 220 },
      properties: { radius: 22, restitution: 1.3, activeImpulse: 350, pointValue: 150, color: '#888888' },
    },
    {
      id: 'asteroid_2',
      type: 'pop_bumper',
      position: { x: 280, y: 200 },
      properties: { radius: 18, restitution: 1.3, activeImpulse: 350, pointValue: 150, color: '#999999' },
    },
    {
      id: 'asteroid_3',
      type: 'pop_bumper',
      position: { x: 200, y: 280 },
      properties: { radius: 25, restitution: 1.3, activeImpulse: 350, pointValue: 150, color: '#777777' },
    },
    // Planet bumpers
    {
      id: 'planet_1',
      type: 'bumper',
      position: { x: 100, y: 380 },
      properties: { radius: 18, restitution: 0.85, pointValue: 75, color: '#4488ff' },
    },
    {
      id: 'planet_2',
      type: 'bumper',
      position: { x: 300, y: 380 },
      properties: { radius: 18, restitution: 0.85, pointValue: 75, color: '#ff4488' },
    },
    // Slingshots (thrusters)
    {
      id: 'thruster_left',
      type: 'slingshot',
      position: { x: 80, y: 620 },
      properties: { width: 40, height: 40, restitution: 0.85, pointValue: 15, color: '#00ffcc' },
    },
    {
      id: 'thruster_right',
      type: 'slingshot',
      position: { x: 280, y: 620 },
      properties: { width: -40, height: 40, restitution: 0.85, pointValue: 15, color: '#00ffcc' },
    },
    // Shield targets (drop targets)
    {
      id: 'shield_1',
      type: 'drop_target',
      position: { x: 150, y: 420 },
      properties: { width: 25, pointValue: 750, group: 'shields', groupIndex: 0, color: '#00ff88' },
    },
    {
      id: 'shield_2',
      type: 'drop_target',
      position: { x: 200, y: 420 },
      properties: { width: 25, pointValue: 750, group: 'shields', groupIndex: 1, color: '#00ff88' },
    },
    {
      id: 'shield_3',
      type: 'drop_target',
      position: { x: 250, y: 420 },
      properties: { width: 25, pointValue: 750, group: 'shields', groupIndex: 2, color: '#00ff88' },
    },
    {
      id: 'shield_4',
      type: 'drop_target',
      position: { x: 300, y: 420 },
      properties: { width: 25, pointValue: 750, group: 'shields', groupIndex: 3, color: '#00ff88' },
    },
    // Wormhole targets
    {
      id: 'wormhole_target',
      type: 'target',
      position: { x: 200, y: 130 },
      properties: { width: 35, pointValue: 500, color: '#cc00ff' },
    },
    // Spinner (satellite)
    {
      id: 'satellite_spinner',
      type: 'spinner',
      position: { x: 250, y: 320 },
      properties: { width: 30, pointValue: 30, color: '#ffcc00' },
    },
    // Wormhole ramps
    {
      id: 'wormhole_entry',
      type: 'ramp',
      position: { x: 130, y: 520 },
      properties: {
        pointValue: 7500,
        path: [{ x: 130, y: 520 }, { x: 100, y: 300 }, { x: 200, y: 130 }],
        color: '#cc00ff',
      },
    },
    {
      id: 'hyperspace_ramp',
      type: 'ramp',
      position: { x: 270, y: 520 },
      properties: {
        pointValue: 7500,
        path: [{ x: 270, y: 520 }, { x: 300, y: 300 }, { x: 200, y: 130 }],
        color: '#0088ff',
      },
    },
    // Orbit (space loop)
    {
      id: 'space_orbit',
      type: 'orbit',
      position: { x: 200, y: 100 },
      properties: {
        pointValue: 4000,
        path: [{ x: 60, y: 300 }, { x: 50, y: 100 }, { x: 200, y: 50 }, { x: 350, y: 100 }, { x: 340, y: 300 }],
        color: '#ffaa00',
      },
    },
    // Lanes (star lanes)
    {
      id: 'star_lane_1',
      type: 'lane',
      position: { x: 80, y: 90 },
      properties: { pointValue: 1500, path: [{ x: 80, y: 90 }, { x: 80, y: 180 }], color: '#ffff44' },
    },
    {
      id: 'star_lane_2',
      type: 'lane',
      position: { x: 200, y: 70 },
      properties: { pointValue: 1500, path: [{ x: 180, y: 70 }, { x: 220, y: 70 }], color: '#ffff44' },
    },
    {
      id: 'star_lane_3',
      type: 'lane',
      position: { x: 320, y: 90 },
      properties: { pointValue: 1500, path: [{ x: 320, y: 90 }, { x: 320, y: 180 }], color: '#ffff44' },
    },
  ],

  flippers: [
    {
      id: 'flipper_left',
      side: 'left',
      pivot: { x: 130, y: 720 },
      length: 60,
      restAngle: Math.PI / 6,
      activeAngle: -Math.PI / 6,
      activationTime: 50,
      returnTime: 80,
    },
    {
      id: 'flipper_right',
      side: 'right',
      pivot: { x: 270, y: 720 },
      length: 60,
      restAngle: Math.PI - Math.PI / 6,
      activeAngle: Math.PI + Math.PI / 6,
      activationTime: 50,
      returnTime: 80,
    },
  ],

  plunger: {
    position: { x: 375, y: 750 },
    laneWidth: 30,
    laneHeight: 100,
    maxLaunchSpeed: 3000,
    chargeTime: 1500,
  },

  drain: {
    position: { x: 200, y: 780 },
    width: 100,
  },

  outlanes: [
    { id: 'outlane_left', side: 'left', path: [{ x: 30, y: 650 }, { x: 30, y: 780 }] },
    { id: 'outlane_right', side: 'right', path: [{ x: 370, y: 650 }, { x: 370, y: 780 }] },
  ],

  inlanes: [
    { id: 'inlane_left', side: 'left', path: [{ x: 80, y: 650 }, { x: 100, y: 700 }] },
    { id: 'inlane_right', side: 'right', path: [{ x: 320, y: 650 }, { x: 300, y: 700 }] },
  ],

  walls: [
    { id: 'wall_left', start: { x: 20, y: 20 }, end: { x: 20, y: 780 }, restitution: 0.5 },
    { id: 'wall_top', start: { x: 20, y: 20 }, end: { x: 395, y: 20 }, restitution: 0.5 },
    { id: 'wall_right', start: { x: 395, y: 20 }, end: { x: 395, y: 780 }, restitution: 0.5 },
    { id: 'wall_plunger_left', start: { x: 350, y: 300 }, end: { x: 350, y: 780 }, restitution: 0.3 },
    { id: 'wall_guide_left', start: { x: 20, y: 780 }, end: { x: 130, y: 740 }, restitution: 0.4 },
    { id: 'wall_guide_right', start: { x: 350, y: 780 }, end: { x: 270, y: 740 }, restitution: 0.4 },
    { id: 'wall_arch_1', start: { x: 393, y: 60 }, end: { x: 390, y: 45 }, restitution: 0.7 },
    { id: 'wall_arch_2', start: { x: 390, y: 45 }, end: { x: 383, y: 32 }, restitution: 0.7 },
    { id: 'wall_arch_3', start: { x: 383, y: 32 }, end: { x: 372, y: 24 }, restitution: 0.7 },
    { id: 'wall_arch_4', start: { x: 372, y: 24 }, end: { x: 358, y: 21 }, restitution: 0.7 },
    { id: 'wall_arch_5', start: { x: 358, y: 21 }, end: { x: 340, y: 25 }, restitution: 0.7 },
    { id: 'wall_arch_6', start: { x: 340, y: 25 }, end: { x: 325, y: 35 }, restitution: 0.7 },
  ],

  scoring: {
    comboWindow: 5000,
    comboMultipliers: [1, 1.5, 2, 3, 4, 5, 6],
    endOfBallBonusBase: 7500,
    jackpotValue: 75000,
  },

  jackpotCondition: {
    type: 'all_ramps',
    requiredElements: ['wormhole_entry', 'hyperspace_ramp', 'space_orbit'],
    description: 'Complete both wormholes and the space orbit for JACKPOT!',
  },

  multiballCondition: {
    type: 'target_bank',
    requiredElements: ['shield_1', 'shield_2', 'shield_3', 'shield_4'],
    ballCount: 3,
    description: 'Destroy all shields for MULTIBALL!',
  },

  theme: {
    backgroundColor: '#000011',
    playfieldColor: '#0a0a2e',
    wallColor: '#3344aa',
    ballColor: '#ffffff',
    flipperColor: '#00ccff',
    fontFamily: '"Orbitron", sans-serif',
    darkModeOverrides: {
      backgroundColor: '#000008',
      playfieldColor: '#050520',
    },
  },
};
